import React, { useState, useContext, useEffect, useCallback, useMemo } from 'react';
import { useField } from 'react-final-form';
import styled from 'styled-components';
import useAvailabilities from '@ubeya/shared/hooks/account/useAvailabilities';
import { getDatesRange } from '@ubeya/shared/constants';
import useAccount from '@ubeya/shared/hooks/account/useAccount';
import useLabels from '@ubeya/shared/hooks/account/useLabels';
import useConvertTimeRange from '@ubeya/shared/hooks/useConvertTimeRange';
import WeekView from 'shared/components/CalendarView/WeekView';
import GroupRenderer from './EmployeeAvailabilityTableRenderer/GroupRenderer';
import DayRenderer from './EmployeeAvailabilityTableRenderer/DayRenderer';
import Header from './Header';
import EmployeeTimeAvailability from 'scheduling/components/WeekView/EmployeeTimeAvailability';
import { SchedulingContextSelector } from 'scheduling/contexts/SchedulingContext';

const Container = styled.div`
  width: 100%;
`;
const EmployeeAvailability = ({ employeeId }) => {
  const {
    input: { value: branches }
  } = useField('branches');
  const { account } = useAccount();
  const [branch, setBranch] = useState(branches?.[0]);
  const [timeRange, setTimeRange] = useState(getDatesRange().thisWeek);
  const { isLoading: isLabelsLoading, mappedLabels, labels } = useLabels({ branchId: branch });
  const { from, to } = useConvertTimeRange(timeRange);
  const { projects, refetch } = useContext(SchedulingContextSelector);
  const { isLoading: isAvailabilitiesLoading, availabilities, updateAvailability } = useAvailabilities({
    start: from,
    end: to,
    branches: [branch],
    employeeIds: [employeeId]
  });

  useEffect(() => {
    if (!branches.includes(branch)) {
      setBranch(branches?.[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branches]);

  const handleAvailabilityChange = useCallback(
    ({ date, labelId, status }) => updateAvailability({ date, labelId, status, employeeId }),
    [employeeId, updateAvailability]
  );

  const weekViewData = useMemo(
    () => labels.map(({ id, background }) => ({ groupId: id, color: background, rows: [{ rowId: 1 }] })),
    [labels]
  );

  const timeRangeFormatted = useMemo(() => ({ startDate: timeRange?.start, endDate: timeRange?.end }), [timeRange]);

  return (
    <Container>
      <Header
        branches={branches}
        branch={branch}
        timeRange={timeRange}
        setTimeRange={setTimeRange}
        setBranch={setBranch}
      />
      {account?.weeklyPreference ? (
        <EmployeeTimeAvailability
          accountId={account?.id}
          employee={{ employeeId }}
          selectedBranchId={branch}
          timeRange={timeRangeFormatted}
        />
      ) : (
        <WeekView
          isLoading={isAvailabilitiesLoading || isLabelsLoading}
          addExtraRow={false}
          showLeftRow={false}
          projects={projects}
          mappedLabels={mappedLabels}
          refetch={refetch}
          labels={labels}
          startDate={timeRange.start.toISOString()}
          endDate={timeRange.end.toISOString()}
          data={weekViewData}
          GroupRenderer={(props) => <GroupRenderer {...props} labels={mappedLabels} />}
          DayRenderer={(props) => (
            <DayRenderer {...props} availabilities={availabilities} onAvailableChange={handleAvailabilityChange} />
          )}
        />
      )}
    </Container>
  );
};

export default React.memo(EmployeeAvailability);
